import { FC, useEffect, useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Paper,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { useDispatch, useSelector } from "react-redux";
import { selectUsersAndLicences } from "../../redux/slices/users/users";
import { LicenseAssignationModalStyles } from "./licenseStyles/LicenseAssignationModal";
import {
  licenseAssignation,
  setLicenseId,
} from "../../redux/slices/licenses/licenseList";
import { OrgGroupTypeIds } from "../../redux/actions/users/userTypes";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { bindLicenseUser } from "../../redux/actions/licenses/licenseListAsyncActions";
import { LoadingButton } from "@mui/lab";
import { TFunction } from "i18next";

interface LicenseAssignationModalProps {
  initData: (dispatch: ThunkDispatch<any, any, any>) => void;
  t: TFunction<"global", undefined>;
  setLoadingAssignLicense: (loading: boolean) => void;
  loadingAssignLicense: boolean;
}

const SetActions = (dispatch: ThunkDispatch<any, any, any>) => {
  return {
    bindLicenseUser: (userGroupId: number, licenseId: string) =>
      dispatch(bindLicenseUser({ userGroupId, licenseId })),

    setLicenseId: (id: string) => dispatch(setLicenseId(id)),
  };
};

const LicenseAssignationModal: FC<LicenseAssignationModalProps> = ({
  initData,
  t,
  setLoadingAssignLicense,
  loadingAssignLicense,
}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const users = useSelector(selectUsersAndLicences);
  const { licenseId } = useSelector(licenseAssignation);
  const [loading, setLoading] = useState(true);
  // const [loadingAssignLicense, setLoadingAssignLicense] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedUserValue, setSelectedUser] = useState<{
    id: string;
    groupId: number | null;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { bindLicenseUser, setLicenseId } = SetActions(dispatch);

  const normalize = /[\u0300-\u036f]/g;
  const usersObject = users
    .filter((user) => (user.licenses ?? []).length === 0)
    .map((user) => ({
      id: user.id,
      username: user.username,
      loc: user.associatedGeoPoint,
      groupId:
        user.groups.find((g) => g.typeId === OrgGroupTypeIds.user)?.id || null,
    }));

  const filteredUsers = usersObject.filter((user) => {
    const searchLower = searchTerm
      .toLowerCase()
      .normalize("NFD")
      .replace(normalize, "");

    const usernameMatch = user.username
      .toLowerCase()
      .normalize("NFD")
      .replace(normalize, "")
      .includes(searchLower);

    const locationMatch = user.loc.some((location) =>
      location.name
        .toLowerCase()
        .normalize("NFD")
        .replace(normalize, "")
        .includes(searchLower),
    );

    return usernameMatch || locationMatch;
  });

  useEffect(() => {
    if (users.length > 0) {
      setLoading(false);
    }
  }, [users]);

  const handleUserSelect = ({
    id,
    groupId,
  }: {
    id: string;
    groupId: number | null;
  }) => {
    setSelectedUser({ id, groupId });
  };

  const handleSuccessTimeout = () => {
    setSelectedUser(null);
    setLicenseId("");
    setSuccess(false);
  };

  const handleSuccess = () => {
    setLoadingAssignLicense(false);
    initData(dispatch);
    setSuccess(true);
  };

  const handleConfirmAssignment = async () => {
    setLoadingAssignLicense(true);
    if (selectedUserValue?.groupId) {
      const response = await bindLicenseUser(
        selectedUserValue.groupId,
        licenseId,
      );
      if (response.meta.requestStatus === "fulfilled") {
        handleSuccess();
        setTimeout(() => {
          handleSuccessTimeout();
        }, 2000);
      } else {
        setLoadingAssignLicense(false);
      }
    }
  };

  const handleCancelSelection = () => {
    setSelectedUser(null);
  };

  const selectedUser = usersObject.find(
    (user) => user.id === selectedUserValue?.id,
  );

  return (
    <Box sx={LicenseAssignationModalStyles.modalContainer}>
      <Typography variant="h5" component="h2" gutterBottom>
        {t("Licenses.assign_license")}
      </Typography>

      {loading ? (
        <Typography>{t("Common_options.Loading")}</Typography>
      ) : usersObject.length === 0 ? (
        <Typography color="text.secondary">
          {t("Licenses.no_users_without_licenses")}
        </Typography>
      ) : (
        <>
          <TextField
            fullWidth
            placeholder={t("Licenses.search_placeholder")}
            value={searchTerm}
            disabled={loadingAssignLicense || success}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={LicenseAssignationModalStyles.searchField}
          />

          {filteredUsers.length === 0 && !!licenseId ? (
            <Typography
              color="text.secondary"
              sx={LicenseAssignationModalStyles.noResultsText}
            >
              {t("Licenses.no_users_found", { searchTerm })}
            </Typography>
          ) : (
            <List sx={LicenseAssignationModalStyles.userList}>
              {filteredUsers.map((user, index) => (
                <Box key={user.id}>
                  <ListItem
                    disabled={loadingAssignLicense || success}
                    disablePadding
                  >
                    <ListItemButton
                      selected={selectedUserValue?.id === user.id}
                      onClick={() =>
                        handleUserSelect({
                          groupId: user.groupId,
                          id: user.id,
                        })
                      }
                    >
                      <ListItemText
                        primary={user.username}
                        secondary={
                          user.loc.length > 0
                            ? `${t("Licenses.location")}: ${user.loc.map((l) => l.name).join(", ")}`
                            : t("Licenses.no_location")
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredUsers.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}

          {selectedUserValue?.groupId && selectedUser && !success ? (
            <Paper
              elevation={3}
              sx={LicenseAssignationModalStyles.confirmationPanel}
            >
              <Typography variant="h6" gutterBottom>
                {t("Licenses.confirm_assignment")}
              </Typography>
              <Typography
                variant="body2"
                sx={LicenseAssignationModalStyles.confirmationText}
              >
                {t("Licenses.assign_to")}:{" "}
                <strong>{selectedUser.username}</strong>
                {selectedUser.loc.length > 0 && (
                  <span>
                    {" "}
                    ({selectedUser.loc.map((l) => l.name).join(", ")})
                  </span>
                )}
              </Typography>
              <Stack direction="row" spacing={2}>
                <LoadingButton
                  loading={loadingAssignLicense}
                  variant="contained"
                  color="primary"
                  sx={LicenseAssignationModalStyles.button}
                  onClick={handleConfirmAssignment}
                  fullWidth
                >
                  {t("Licenses.assign_button")}
                </LoadingButton>
                <Button
                  disabled={loadingAssignLicense}
                  variant="outlined"
                  color="inherit"
                  sx={LicenseAssignationModalStyles.button}
                  onClick={handleCancelSelection}
                  fullWidth
                >
                  {t("Common_options.Cancel")}
                </Button>
              </Stack>
            </Paper>
          ) : success ? (
            <Alert severity="success">
              {t("Licenses.success_assignation")}
            </Alert>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default LicenseAssignationModal;
